import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { useStyles } from "./myLearningStyle";
import { useTranslation } from "react-i18next";
import {
  SchoolOutlined as SchoolIcon,
  Telegram as TelegramIcon,
  Assignment as AssignmentIcon,
  PersonOutlined as UserIcon,
  LoginOutlined as LogoutIcon,
  ListOutlined as ListOutlinedIcon,
  NotificationsNoneOutlined as NotificationAddOutlinedIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  EventNoteOutlined as EventNoteOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { HEADER_CLICK_TYPES, MY_LEARNING_KEYS, MyLearning_Link_Paths } from "utils/constants";
import { Badge, Button, List, ListItem } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import portalSettingsManager from "utils/portalSettingsManager";
import { handleMenuKeyDown, handleNewLearningRequestClick } from "utils/helpers";

type MyLearningProps = {
  onClick: (val: string) => void;
  showNotification: boolean;
  showServiceNowMenu: boolean;
  showAttendanceLink: boolean;
  newNotificationCount?: number;
  setOpenNotification: (val: boolean) => void;
  onOpenNewLearningStepSurvey: () => void;
};

export const MyLearning = ({
  onClick,
  showNotification,
  showServiceNowMenu,
  showAttendanceLink,
  newNotificationCount,
  setOpenNotification,
  onOpenNewLearningStepSurvey,
}: MyLearningProps): React.ReactElement => {
  const classes = useStyles();
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const menuListId = "menu-list-grow";
  const visibleIds = [];
  const listRefs = useRef(new Map());
  const myLearningId = portalSettingsManager?.application?.features?.enableMyLearningForIBF
    ? "my-learning"
    : "my-profile";
  const buttonId = `btn-${myLearningId}`;

  const MY_BOOKINGS = "my_bookings";

  visibleIds.push(MY_LEARNING_KEYS.PROFILE);
  portalSettingsManager?.application?.features?.enableMyLearningForIBF &&
    visibleIds.push(MY_LEARNING_KEYS.MY_LEARNING);
  visibleIds.push(MY_LEARNING_KEYS.MY_BOOKINGS);
  showNotification && visibleIds.push(MY_LEARNING_KEYS.NOTIFICATIONS);
  showServiceNowMenu && visibleIds.push(MY_LEARNING_KEYS.INCIDENT_MGMT);
  showAttendanceLink && visibleIds.push(MY_LEARNING_KEYS.MANAGE_ATTENDANCE);
  visibleIds.push(MY_LEARNING_KEYS.LEARNING_REQUEST);
  visibleIds.push(MY_LEARNING_KEYS.CONTACT_US);
  visibleIds.push(MY_LEARNING_KEYS.LOGOUT);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  const handleNavigate = (path: string) => {
    if (location.pathname !== path) {
      navigate(path);
      setOpen(false);
    }
  };

  const findCurrentActiveElement = () => {
    const liElement = listRefs.current[visibleIds[selectedIndex]];
    if (liElement) {
      const buttonElement = liElement.querySelector("button");
      return buttonElement;
    } else return null;
  };

  const handlePopperKeyDown = (e: {
    key: string;
    shiftKey: boolean;
    preventDefault: () => void;
    stopPropagation: () => void;
  }) =>
    handleMenuKeyDown(
      e,
      handleClose,
      visibleIds,
      buttonId,
      setSelectedIndex,
      findCurrentActiveElement,
    );

  useEffect(() => {
    const handleFocusOut = (e) => {
      if (!e.relatedTarget?.closest(`#${menuListId}`)) {
        setOpen(false);
      }
    };
    window.addEventListener("focusout", handleFocusOut);
    return () => {
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const isSelected = (id: string) => visibleIds[selectedIndex] === id;

  useEffect(() => {
    if (open) {
      const element = document.querySelector(`#${menuListId} > li`).firstChild as HTMLLinkElement;
      element?.focus();
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    const element = findCurrentActiveElement();
    element?.focus();
  }, [selectedIndex]);

  return (
    <>
      <Button
        id={buttonId}
        aria-controls={open ? `${myLearningId}-menu-id` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : "false"}
        variant="text"
        className={classes.menuButton}
        disableElevation
        endIcon={open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        ref={anchorRef}
        onMouseEnter={handleOpen}
        onClick={handleOpen}
        onMouseLeave={handleClose}
      >
        {portalSettingsManager?.application?.features?.enableMyLearningForIBF
          ? t("my_learning")
          : t("my_profile")}
      </Button>
      <div className={classes.root}>
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          id={`${myLearningId}-menu-id`}
          onKeyDown={(e) => handlePopperKeyDown(e)}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper
                elevation={24}
                className={classes.paperBorder}
                onMouseEnter={handleOpen}
                onMouseLeave={handleClose}
              >
                <List className={classes.profileMenu} id={menuListId}>
                  <ListItem
                    aria-label={t("profile")}
                    className={
                      isSelected(MY_LEARNING_KEYS.PROFILE)
                        ? classes.menuStyle + " " + classes.focus
                        : classes.menuStyle
                    }
                    id={MY_LEARNING_KEYS.PROFILE}
                    ref={(el) => {
                      listRefs.current[MY_LEARNING_KEYS.PROFILE] = el;
                    }}
                  >
                    <Button
                      className={classes.liButton}
                      disableFocusRipple
                      disableRipple
                      onClick={() => handleNavigate(MyLearning_Link_Paths.Profile)}
                      startIcon={<UserIcon className={classes.iconStyle} />}
                    >
                      {t("profile")}
                    </Button>
                  </ListItem>
                  {portalSettingsManager?.application?.features?.enableMyLearningForIBF && (
                    <ListItem
                      aria-label={t("my_learning")}
                      className={
                        isSelected(MY_LEARNING_KEYS.MY_LEARNING)
                          ? classes.menuStyle + " " + classes.focus
                          : classes.menuStyle
                      }
                      id={MY_LEARNING_KEYS.MY_LEARNING}
                      ref={(el) => {
                        listRefs.current[MY_LEARNING_KEYS.MY_LEARNING] = el;
                      }}
                    >
                      <Button
                        className={classes.liButton}
                        disableFocusRipple
                        disableRipple
                        onClick={() => handleNavigate(MyLearning_Link_Paths.MyLearning)}
                        startIcon={<SchoolIcon className={classes.iconStyle} />}
                      >
                        {t("my_learning")}
                      </Button>
                    </ListItem>
                  )}

                  <ListItem
                    aria-label={t(MY_BOOKINGS)}
                    className={
                      isSelected(MY_LEARNING_KEYS.MY_BOOKINGS)
                        ? classes.menuStyle + " " + classes.focus
                        : classes.menuStyle
                    }
                    id={MY_LEARNING_KEYS.MY_BOOKINGS}
                    ref={(el) => {
                      listRefs.current[MY_LEARNING_KEYS.MY_BOOKINGS] = el;
                    }}
                  >
                    <Button
                      className={classes.liButton}
                      disableFocusRipple
                      disableRipple
                      onClick={() => handleNavigate("/mybookings")}
                      startIcon={<EventNoteOutlinedIcon className={classes.iconStyle} />}
                    >
                      {t(MY_BOOKINGS)}
                    </Button>
                  </ListItem>

                  {showNotification && (
                    <ListItem
                      aria-label={t("notifications")}
                      className={
                        isSelected(MY_LEARNING_KEYS.NOTIFICATIONS)
                          ? classes.menuStyle + " " + classes.focus
                          : classes.menuStyle
                      }
                      id={MY_LEARNING_KEYS.NOTIFICATIONS}
                      ref={(el) => {
                        listRefs.current[MY_LEARNING_KEYS.NOTIFICATIONS] = el;
                      }}
                    >
                      <Button
                        className={classes.liButton}
                        disableFocusRipple
                        disableRipple
                        onClick={() => setOpenNotification(true)}
                        startIcon={
                          <Badge
                            badgeContent={newNotificationCount}
                            color="secondary"
                            classes={{ anchorOriginTopRightRectangular: classes.anchorTopRight }}
                            className={`${classes.iconStyle}`}
                          >
                            <NotificationAddOutlinedIcon />
                          </Badge>
                        }
                      >
                        {t("notifications")}
                      </Button>
                    </ListItem>
                  )}

                  {showServiceNowMenu && (
                    <ListItem
                      aria-label={t("incident_management")}
                      className={
                        isSelected(MY_LEARNING_KEYS.INCIDENT_MGMT)
                          ? classes.menuStyle + " " + classes.focus
                          : classes.menuStyle
                      }
                      id={MY_LEARNING_KEYS.INCIDENT_MGMT}
                      ref={(el) => {
                        listRefs.current[MY_LEARNING_KEYS.INCIDENT_MGMT] = el;
                      }}
                    >
                      <Button
                        className={classes.liButton}
                        disableFocusRipple
                        disableRipple
                        onClick={() => handleNavigate(HEADER_CLICK_TYPES.SERVICENOW)}
                        startIcon={<ListOutlinedIcon className={classes.iconStyle} />}
                      >
                        {t("incident_management")}
                      </Button>
                    </ListItem>
                  )}

                  {showAttendanceLink && (
                    <ListItem
                      aria-label={t("manage_attendance_completion_menu")}
                      className={
                        isSelected(MY_LEARNING_KEYS.MANAGE_ATTENDANCE)
                          ? classes.menuStyle + " " + classes.focus
                          : classes.menuStyle
                      }
                      id={MY_LEARNING_KEYS.MANAGE_ATTENDANCE}
                      ref={(el) => {
                        listRefs.current[MY_LEARNING_KEYS.MANAGE_ATTENDANCE] = el;
                      }}
                    >
                      <Button
                        className={classes.liButton}
                        disableFocusRipple
                        disableRipple
                        onClick={() => handleNavigate(MyLearning_Link_Paths.AttendanceConfirmation)}
                        startIcon={<ListOutlinedIcon className={classes.iconStyle} />}
                      >
                        {t("manage_attendance_completion_menu")}
                      </Button>
                    </ListItem>
                  )}

                  <ListItem
                    aria-label={t("learning_request")}
                    className={
                      isSelected(MY_LEARNING_KEYS.LEARNING_REQUEST)
                        ? classes.menuStyle + " " + classes.focus
                        : classes.menuStyle
                    }
                    id={MY_LEARNING_KEYS.LEARNING_REQUEST}
                    ref={(el) => {
                      listRefs.current[MY_LEARNING_KEYS.LEARNING_REQUEST] = el;
                    }}
                  >
                    <Button
                      className={classes.liButton}
                      disableFocusRipple
                      disableRipple
                      onClick={() =>
                        handleNewLearningRequestClick(onOpenNewLearningStepSurvey, handleNavigate)
                      }
                      startIcon={<AssignmentIcon className={classes.iconStyle} />}
                    >
                      {t("learning_request")}
                    </Button>
                  </ListItem>

                  <ListItem
                    aria-label={t("contact_us")}
                    className={
                      isSelected(MY_LEARNING_KEYS.CONTACT_US)
                        ? classes.menuStyle + " " + classes.focus
                        : classes.menuStyle
                    }
                    id={MY_LEARNING_KEYS.CONTACT_US}
                    ref={(el) => {
                      listRefs.current[MY_LEARNING_KEYS.CONTACT_US] = el;
                    }}
                  >
                    <Button
                      className={classes.liButton}
                      disableFocusRipple
                      disableRipple
                      onClick={() => onClick(HEADER_CLICK_TYPES.CONTACTUS)}
                      startIcon={<TelegramIcon className={classes.iconStyle} />}
                    >
                      {t("contact_us")}
                    </Button>
                  </ListItem>

                  <ListItem
                    aria-label={t("log_out")}
                    className={
                      isSelected(MY_LEARNING_KEYS.LOGOUT)
                        ? classes.menuStyle + " " + classes.focus
                        : classes.menuStyle
                    }
                    id={MY_LEARNING_KEYS.LOGOUT}
                    ref={(el) => {
                      listRefs.current[MY_LEARNING_KEYS.LOGOUT] = el;
                    }}
                  >
                    <Button
                      className={classes.liButton}
                      disableFocusRipple
                      disableRipple
                      onClick={() => onClick(HEADER_CLICK_TYPES.LOGOUT)}
                      startIcon={<LogoutIcon className={classes.iconStyle} />}
                    >
                      {t("log_out")}
                    </Button>
                  </ListItem>
                </List>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>
    </>
  );
};
